import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { closeModal, openModal } from "risa-oasis-ui_v2";
import CustomTable from "../components/custom-table/custom-table";
import { LoaderMessage } from "../components/loaderMessage/loaderMessage";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreDocumentReference } from "../firebase/references";
import {
  inviteMember,
  mapApiRoleIdToUiRole,
  mapUiRoleToApiRoleId,
  removeMember,
  updateMemberRole,
} from "../services/memberManagementService";

import AddMemberModalNew, {
  AddMemberFormData,
} from "../components/modals/addMemberModal/addMemberModalNew";
import DeleteUserModal from "../components/modals/deleteUserModal/deleteUserModal";
import UpdateUserRoleModal from "../components/modals/updateUserRoleModal/updateUserRoleModal";
import { ToastMessages } from "../components/toasts/toastMessages";
import { getAllProviderDetailsForClinic } from "../state/allProvidersSlice";
import { useAuthDispatch, useAuthSelector } from "../state/AuthStoreProvider";
import type { AuthRootState } from "../state/store";
import type { RoleOption } from "../types";
import {
  generateUIDataForMemberTable,
  MemberTableHeaderColumns,
} from "./memberData";

export interface MemberModel {
  email: string;
  name: string;
  facilityId: string;
  roleId: string;
  status: string;
  isAdmin: boolean;
  id?: string;
}

interface MembersPageProps {
  roleOptions?: RoleOption[];
}

const MembersPage = ({ roleOptions }: MembersPageProps) => {
  const { data: singleProviderDetails } = useAuthSelector(
    (state: AuthRootState) => state.providerDetails,
  );

  const { data: allProviderDetails } = useAuthSelector(
    (state: AuthRootState) => state.allProviders,
  );

  const dispatch = useAuthDispatch();
  const [tableData, setTableData] = useState<any[]>([]);
  const [filteredTableData, setFilteredTableData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [facilityName, setFacilityName] = useState<string>("");

  const [memberToDelete, setMemberToDelete] = useState<MemberModel | null>(
    null,
  );
  const [memberToUpdateRole, setMemberToUpdateRole] =
    useState<MemberModel | null>(null);

  const openAddMemberModal = () => {
    openModal("add-member-modal");
  };

  const closeAddMemberModal = () => {
    closeModal("add-member-modal");
  };

  useEffect(() => {
    if (singleProviderDetails?.FacilityId) {
      dispatch(
        getAllProviderDetailsForClinic(singleProviderDetails.FacilityId) as any,
      );
    }
  }, [singleProviderDetails, dispatch]);

  useEffect(() => {
    const fetchFacilityName = async () => {
      if (singleProviderDetails?.FacilityId) {
        try {
          const facility = await FirestoreService.getDocument<{ Name: string }>(
            FirestoreDocumentReference.healthFacility(
              singleProviderDetails.FacilityId,
            ),
          );
          if (facility?.Name) {
            setFacilityName(facility.Name);
          }
        } catch (error) {
          console.error("Error fetching facility name:", error);
        }
      }
    };
    fetchFacilityName();
  }, [singleProviderDetails?.FacilityId]);

  useEffect(() => {
    if (allProviderDetails) {
      const members: MemberModel[] = allProviderDetails
        .filter((provider: any) => provider.Status !== "disabled")
        .map((provider: any) => ({
          id: provider.id || "",
          email: provider.email || provider.EmailAddresses?.[0] || "",
          name: provider.name || provider.FirstName || "--",
          roleId: mapApiRoleIdToUiRole(provider.role_id ?? ""),
          status: provider.Status || "",
          facilityId: provider.FacilityId || "",
          isAdmin: provider.IsAdmin || false,
        }))
        .sort((a: MemberModel, b: MemberModel) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      setTableData(
        generateUIDataForMemberTable(members, {
          onEditMember: (member) => setMemberToUpdateRole(member),
          onDeleteMember: (member) => setMemberToDelete(member),
        }),
      );
    }
  }, [allProviderDetails]);

  useEffect(() => {
    if (searchText.trim() === "") {
      setFilteredTableData(tableData);
    } else {
      const lowerSearchText = searchText.toLowerCase();
      const filtered = tableData.filter((row: any) => {
        const name = (row.name || "").toLowerCase();
        const email = (row.email || "").toLowerCase();
        return (
          name.includes(lowerSearchText) || email.includes(lowerSearchText)
        );
      });
      setFilteredTableData(filtered);
    }
  }, [tableData, searchText]);

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  return (
    <>
      <div className="member-container">
        <h1>{facilityName && <span>{facilityName} </span>}Members</h1>
        {tableData.length > 0 ? (
          <CustomTable
            tableHeaders={MemberTableHeaderColumns}
            tableData={filteredTableData}
            count={filteredTableData.length}
            itemsPerPage={15}
            pagesPerView={5}
            tableName="members"
            searchingText={handleSearch}
            placeholder="Search by name or email"
            showOtherButton={true}
            otherButtonMeta={{
              text: "Add Members",
              onClick: openAddMemberModal,
              disabled: false,
              buttonType: "secondary",
              size: "medium",
            }}
          />
        ) : (
          <LoaderMessage message={"Fetching members data..."} />
        )}
        <div>
          {singleProviderDetails?.FacilityId && (
            <AddMemberModalNew
              roleOptions={roleOptions}
              onSave={async function (
                formData: AddMemberFormData,
              ): Promise<void> {
                closeAddMemberModal();
                try {
                  await inviteMember(singleProviderDetails.FacilityId || "", {
                    name: formData.name,
                    email: formData.email,
                    roleId: roleOptions
                      ? (formData.role as any)
                      : mapUiRoleToApiRoleId(
                          formData.role as "Admin" | "Member",
                        ),
                  });
                  toast.success(ToastMessages.MEMBER_INVITE_SUCCESS);
                  dispatch(
                    getAllProviderDetailsForClinic(
                      singleProviderDetails.FacilityId || "",
                    ) as any,
                  );
                } catch (error: any) {
                  toast.error(ToastMessages.MEMBER_INVITE_FAILED);
                }
              }}
              onClose={closeAddMemberModal}
            />
          )}

          <UpdateUserRoleModal
            roleOptions={roleOptions}
            isOpen={memberToUpdateRole !== null}
            memberId={memberToUpdateRole?.id || ""}
            currentRole={memberToUpdateRole?.roleId}
            memberName={memberToUpdateRole?.name}
            onSave={async (memberId: string, newRole: string) => {
              try {
                await updateMemberRole(
                  singleProviderDetails?.FacilityId || "",
                  memberId,
                  {
                    roleId: roleOptions
                      ? (newRole as any)
                      : mapUiRoleToApiRoleId(newRole as "Admin" | "Member"),
                  },
                );
                toast.success(ToastMessages.MEMBER_ROLE_UPDATE_SUCCESS);
                dispatch(
                  getAllProviderDetailsForClinic(
                    singleProviderDetails?.FacilityId || "",
                  ) as any,
                );
              } catch (error) {
                toast.error(ToastMessages.MEMBER_ROLE_UPDATE_FAILED);
              }
            }}
            onClose={() => setMemberToUpdateRole(null)}
          />

          <DeleteUserModal
            isOpen={memberToDelete !== null}
            memberId={memberToDelete?.id || ""}
            memberName={memberToDelete?.name}
            onDelete={async (memberId: string) => {
              try {
                await removeMember(
                  singleProviderDetails?.FacilityId || "",
                  memberId,
                );
                toast.success(ToastMessages.MEMBER_REMOVED_SUCCESS);
                dispatch(
                  getAllProviderDetailsForClinic(
                    singleProviderDetails?.FacilityId || "",
                  ) as any,
                );
              } catch (error) {
                toast.error(ToastMessages.MEMBER_REMOVED_FAILED);
              }
            }}
            onClose={() => setMemberToDelete(null)}
          />
        </div>
      </div>
    </>
  );
};

export default MembersPage;
